import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Crown, UserMinus, UserPlus } from "lucide-react";
import { axiosPrivate } from "../../api/axios";
import { squadService, apiErrorMessage } from "../../api/taskBoardService";
import Avatar from "../ui/Avatar";
import Button from "../forms/Button";
import ConfirmModal from "../modals/ConfirmModal";
import { SQUAD_ROLES } from "./constants";
import { formatDate } from "./shared";
import MemberSummary from "./MemberSummary";
import StatusBar from "./StatusBar";
import { forAssignee, sprintTasks, summarize } from "./squadStats";

const COLUMNS = "minmax(0,2fr) 60px minmax(0,2fr) 56px 56px 66px 82px 28px";

const Th = ({ children, right }) => (
  <span className={`text-[11px] uppercase tracking-wide text-faint whitespace-nowrap ${right ? "text-right" : ""}`}>{children}</span>
);

const sprintRange = (s) =>
  `${formatDate(s.startDate, { day: "numeric", month: "short" })} – ${formatDate(s.endDate, { day: "numeric", month: "short" })}`;

const RoleSelect = ({ squadId, member, busy, run }) => (
  <select
    value={member.role}
    disabled={busy}
    onChange={(ev) => run(() => squadService.updateMemberRole(squadId, member.employee._id, ev.target.value))}
    className="h-9 flex-1 min-w-0 cursor-pointer appearance-none rounded-xl pl-3 pr-8 text-[13px] text-ink outline-none focus:border-[color:var(--accent-line)]"
    style={{
      background: "var(--panel2) url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2378746e' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\") no-repeat right 10px center",
      border: "1px solid var(--line2)",
    }}
    aria-label={`Role of ${member.employee.fullNameEn}`}
  >
    {SQUAD_ROLES.map((r) => (
      <option key={r.value} value={r.value}>{r.label}</option>
    ))}
  </select>
);

/**
 * Squad membership table with per-member workload for the selected sprint
 * scope. Admins (high_potit) can add members, change roles and remove members;
 * everyone else sees a read-only table. The server enforces this regardless.
 * `squadTasks` are the squad's board tasks; `scopeTasks` those in the scope.
 */
const SquadMembers = ({ squad, admin, onChanged, onError, squadTasks, scopeTasks, sprintList, scopeIds, currentId, overdueId, scopeName, onOpenTask }) => {
  const [employees, setEmployees] = useState([]);
  const [adding, setAdding] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [role, setRole] = useState("MEMBER");
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [showAll, setShowAll] = useState({}); // per member: full sprint list

  useEffect(() => {
    if (!admin) return;
    axiosPrivate
      .get("/api/employees")
      .then((res) => setEmployees(Array.isArray(res.data) ? res.data : res.data?.data || []))
      .catch((err) => onError(apiErrorMessage(err, "Could not load employees")));
  }, [admin]); // eslint-disable-line react-hooks/exhaustive-deps

  const memberIds = useMemo(() => new Set(squad.members.map((m) => m.employee?._id)), [squad.members]);
  const candidates = employees
    .filter((e) => !memberIds.has(e._id))
    .sort((a, b) => (a.fullNameEn || "").localeCompare(b.fullNameEn || ""));

  const run = async (fn) => {
    setBusy(true);
    try {
      const res = await fn();
      onChanged(res.data);
    } catch (err) {
      onError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const rows = useMemo(
    () =>
      squad.members
        .filter((m) => m.employee)
        .map((m) => ({ member: m, ...summarize(forAssignee(scopeTasks, m.employee._id), overdueId) }))
        .sort((a, b) => b.tasks - a.tasks),
    [squad.members, scopeTasks, overdueId],
  );
  // Newest first. Default: 4 newest, or the in-scope sprints (still capped at 4);
  // a single older sprint replaces the 4th row.
  const newest = useMemo(() => [...sprintList].reverse(), [sprintList]);
  const defaultSprints = useMemo(() => {
    if (scopeIds.size > 1 && scopeIds.size < sprintList.length) return newest.filter((s) => scopeIds.has(s._id)).slice(0, 4);
    const top = newest.slice(0, 4);
    if (scopeIds.size === 1 && !top.some((s) => scopeIds.has(s._id))) {
      const picked = newest.find((s) => scopeIds.has(s._id));
      if (picked) return [...top.slice(0, 3), picked];
    }
    return top;
  }, [newest, scopeIds, sprintList.length]);
  const busiest = Math.max(1, ...rows.map((r) => r.tasks));

  return (
    <section
      style={{ background: "var(--panel)", border: "1px solid var(--line)", borderRadius: 18, backdropFilter: "blur(20px)", boxShadow: "var(--card-shadow)" }}
      className="overflow-x-auto"
    >
      <div style={{ minWidth: 820 }}>
        <div className="flex items-center justify-between gap-2 p-4 sm:p-5">
          <h2 className="font-display text-[15px] font-semibold text-ink whitespace-nowrap">Members ({squad.members.length})</h2>
          {admin && (
            <button
              type="button"
              onClick={() => setAdding((v) => !v)}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium px-3 py-1.5 rounded-xl whitespace-nowrap"
              style={{ background: "var(--accent-soft)", color: "var(--accent-ink)" }}
            >
              <UserPlus className="w-4 h-4" /> Add member
            </button>
          )}
        </div>

        {admin && adding && (
          <form
            className="flex gap-2 px-5 pb-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!employeeId) return;
              run(() => squadService.addMember(squad._id, employeeId, role)).then(() => setEmployeeId(""));
            }}
          >
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="input-field flex-1 min-w-0" aria-label="Employee to add">
              <option value="">Select an employee…</option>
              {candidates.map((e) => (
                <option key={e._id} value={e._id}>
                  {e.fullNameEn}
                  {e.nickname ? ` (${e.nickname})` : ""}
                </option>
              ))}
            </select>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="input-field w-40" aria-label="Squad role">
              {SQUAD_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <Button type="submit" size="sm" disabled={!employeeId || busy}>Add</Button>
          </form>
        )}

        {rows.length === 0 ? (
          <p className="px-5 pb-5 text-sm italic text-faint">No members yet.</p>
        ) : (
          <>
            <div className="grid items-center gap-3 px-5 py-2" style={{ gridTemplateColumns: COLUMNS, borderTop: "1px solid var(--line)" }}>
              <Th>Member</Th><Th right>Tasks</Th><Th>Breakdown</Th><Th right>Done</Th><Th right>Open</Th><Th right>Overdue</Th><Th right>Complete</Th><span />
            </div>
            {rows.map((r) => {
              const e = r.member.employee;
              const open = expanded === e._id;
              const mine = forAssignee(sprintTasks(squadTasks), e._id);
              const total = mine.length;
              const doneIn = (sp) => mine.filter((t) => (t.sprint?._id || t.sprint) === sp._id && t.status === "done").length;
              const best = sprintList.reduce((top, sp) => (doneIn(sp) > (top ? doneIn(top) : 0) ? sp : top), null);
              return (
                <div key={e._id} style={{ borderTop: "1px solid var(--line)", background: open ? "var(--panel2)" : "transparent" }}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpanded(open ? null : e._id)}
                    onKeyDown={(ev) => (ev.key === "Enter" || ev.key === " ") && (ev.preventDefault(), setExpanded(open ? null : e._id))}
                    className="grid items-center gap-3 px-5 py-3 cursor-pointer hover:bg-hover"
                    style={{ gridTemplateColumns: COLUMNS }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar src={e.photo} name={e.fullNameEn} size="md" preview={false} className="!w-[34px] !h-[34px] shrink-0" />
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 text-sm font-medium text-ink min-w-0">
                          <span className="truncate">{e.fullNameEn}</span>
                          {r.member.role === "SQUAD_LEAD" && (
                            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: "var(--accent-soft)", color: "var(--accent-ink)" }}>
                              <Crown className="w-3 h-3" /> Lead
                            </span>
                          )}
                        </p>
                        {e.email && <p className="text-xs text-faint truncate">{e.email}</p>}
                      </div>
                    </div>
                    <span className="font-display text-right" style={{ fontSize: 17, fontVariantNumeric: "tabular-nums" }}>{r.tasks}</span>
                    <StatusBar byStatus={r.byStatus} total={r.tasks} scale={r.tasks / busiest} titled />
                    <span className="text-right text-sm tabular-nums" style={{ color: "var(--green)" }}>{r.done}</span>
                    <span className="text-right text-sm tabular-nums">{r.open}</span>
                    <span className="text-right text-sm tabular-nums" style={{ color: r.overdue > 0 ? "var(--red)" : "var(--muted)" }}>{r.overdue}</span>
                    <span className="text-right text-sm tabular-nums" style={{ color: "var(--accent-ink)" }}>{r.pct}%</span>
                    <ChevronDown className="w-4 h-4 text-faint transition-transform" style={{ transform: open ? "rotate(180deg)" : "none" }} />
                  </div>

                  {open && (
                    <div className="flex flex-col" style={{ padding: "4px 20px 20px 65px", gap: 18, animation: "wsIn .2s ease both" }}>
                      <MemberSummary
                        member={e}
                        stats={r}
                        scopeTasks={forAssignee(scopeTasks, e._id)}
                        scopeText={scopeIds.size > 1 ? `across ${scopeIds.size} sprints` : `in ${scopeName}`}
                        openTasks={overdueId ? mine.filter((t) => (t.sprint?._id || t.sprint) === currentId && t.status !== "done") : null}
                        onOpenTask={onOpenTask}
                      />
                      <div className="grid gap-6" style={{ gridTemplateColumns: "minmax(0,2fr) minmax(0,1fr)" }}>
                      <div className="space-y-2.5 min-w-0">
                        <div className="space-y-2.5" style={{ maxHeight: 340, overflowY: "auto" }}>
                        {(showAll[e._id] ? newest : defaultSprints).map((s) => {
                          const st = summarize(mine.filter((t) => (t.sprint?._id || t.sprint) === s._id), currentId);
                          const inScope = scopeIds.has(s._id);
                          return (
                            <div key={s._id} className="flex items-center gap-3" style={{ opacity: inScope ? 1 : 0.45 }}>
                              <div className="w-44 shrink-0 min-w-0">
                                <p className="text-[13px] text-ink flex items-center gap-2">
                                  <span className="truncate">{s.name}</span>
                                  {s._id === currentId && (
                                    <span className="text-[10.5px] px-1.5 py-0.5 rounded-full" style={{ background: "var(--accent-soft)", color: "var(--accent-ink)" }}>Current</span>
                                  )}
                                </p>
                                <p className="text-[11.5px] text-faint whitespace-nowrap">{sprintRange(s)}</p>
                              </div>
                              <div className="flex-1 min-w-0"><StatusBar byStatus={st.byStatus} total={st.tasks} height={6} /></div>
                              <span className="text-[12px] text-muted whitespace-nowrap tabular-nums">{st.done} / {st.tasks} done</span>
                            </div>
                          );
                        })}
                        </div>
                        {sprintList.length > defaultSprints.length && (
                          <button
                            type="button"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setShowAll((prev) => ({ ...prev, [e._id]: !prev[e._id] }));
                            }}
                            className="text-[12.5px] font-semibold"
                            style={{ color: "var(--accent-ink)" }}
                          >
                            {showAll[e._id] ? "Show fewer" : `Show all ${sprintList.length} sprints`}
                          </button>
                        )}
                      </div>
                      <div className="pl-6 pr-1 space-y-2.5 text-sm self-start" style={{ borderLeft: "1px solid var(--line)" }}>
                        <p className="flex justify-between text-muted">All sprints <b className="text-ink">{total} tasks</b></p>
                        <p className="flex justify-between text-muted">Avg per sprint <b className="text-ink">{sprintList.length ? (total / sprintList.length).toFixed(1) : "0.0"}</b></p>
                        <p className="flex justify-between text-muted">Best sprint <b className="text-ink">{best?.name || "—"}</b></p>
                        {admin && (
                          <div className="flex items-center gap-2 pt-3 mt-1" style={{ borderTop: "1px solid var(--line)" }}>
                            <RoleSelect squadId={squad._id} member={r.member} busy={busy} run={run} />
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => setRemoving(e)}
                              className="grid h-9 w-9 shrink-0 place-items-center rounded-xl transition-colors hover:bg-[color:var(--red-soft)]"
                              style={{ border: "1px solid var(--red)", color: "var(--red)" }}
                              aria-label={`Remove ${e.fullNameEn}`}
                              title="Remove from squad"
                            >
                              <UserMinus className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        <p className="px-5 py-3 text-xs text-faint" style={{ borderTop: "1px solid var(--line)" }}>
          Squad Leads see every task of this squad. Members only see tasks assigned to them.
        </p>
      </div>

      <ConfirmModal
        isOpen={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          const target = removing;
          setRemoving(null);
          run(() => squadService.removeMember(squad._id, target._id));
        }}
        title="Remove member?"
        message={`${removing?.fullNameEn || "This person"} will lose access to this squad's tasks immediately. Their tasks are kept (still assigned) so a lead can reassign them.`}
        confirmText="Remove"
      />
    </section>
  );
};

export default SquadMembers;
